import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { LocalStorageService } from "./localStorage.service";

/**
 * Manages the user's persistent opt-in for diagnostic auto-upload.
 *
 * Model:
 *   - Auto-upload of error logs is gated SOLELY by user consent. Once the
 *     user flips the "Aide au support technique" toggle ON, error logs are
 *     uploaded automatically until they flip it OFF.
 *   - Server-side targeting (`diagnostic_targets` / auto-send-status) no
 *     longer gates uploads. It is kept ONLY to drive the in-app
 *     consent-request prompt: when support flags a cohort
 *     (user / app version / platform / device model), devices in that
 *     cohort that have NOT consented yet see a polite opt-in prompt.
 *
 * Compliance (Apple App Review 5.1 / Google Play User Data):
 *   1. The user MUST explicitly grant consent in Settings (toggle OFF by
 *      default; revocable any time with the same toggle).
 *   2. Consent is persistent + versioned. If the disclosure text changes
 *      (e.g. a new third-party sub-processor), CURRENT_POLICY_VERSION is
 *      bumped and old consents are invalidated — the user re-consents.
 *   3. The consent state is visible in Configurations → Aide au support
 *      technique, and revocable from there.
 */

export interface ConsentState {
  granted: boolean;
  /** Policy version the user consented to. */
  version: string | null;
  /** Granted-at timestamp (ms). */
  grantedAt: number | null;
}

export interface AutoSendStatus {
  /** True iff support has targeted this device's cohort, regardless of consent. */
  matched: boolean;
  /** True iff matched AND user has granted consent on this device. */
  active: boolean;
  expiresAt?: string;   // ISO timestamp
  reason?: string;      // human-readable, shown in banner / prompt
  promptContext?: "first_launch" | "support_target";
}

@Injectable({ providedIn: "root" })
export class DiagnosticsConsentService {
  /**
   * Bump this when the disclosure copy meaningfully changes. Old consents
   * become invalid — the user must re-grant on the new text.
   */
  static readonly CURRENT_POLICY_VERSION = "2026-05-21";
  private static readonly STORAGE_KEY = "diagnostics_consent";
  // Bumped to _v2 to invalidate caches written before the expires_at →
  // expiresAt mapping fix (those entries had undefined expiry and would
  // resurrect `active: true` forever).
  private static readonly STATUS_CACHE_KEY = "diagnostics_status_cache_v2";
  private static readonly DISMISS_KEY = "diagnostics_consent_prompt_dismissed_until";
  private static readonly FIRST_LAUNCH_PROMPT_KEY = "diagnostics_first_launch_prompt_seen";
  /** Poll status this often (ms). Avoid hammering — server hint is slow-changing. */
  private static readonly POLL_INTERVAL_MS = 10 * 60 * 1000;   // 10 min

  private consent$ = new BehaviorSubject<ConsentState>(this.loadConsent());
  private status$ = new BehaviorSubject<AutoSendStatus>({ matched: false, active: false });
  /** Combined: { allowed: <consent granted>, status }. `status` is kept for
   *  the Settings UI but no longer gates `allowed`. */
  private effective$ = new BehaviorSubject<{ allowed: boolean; status: AutoSendStatus }>({
    allowed: false,
    status: { matched: false, active: false },
  });
  /** Fires once whenever the server starts matching this device but consent
   * is OFF — the caller (AppComponent) shows a non-blocking in-app prompt
   * asking the user to opt-in. Cleared on consent grant/revoke or dismiss. */
  private consentRequestSubject = new BehaviorSubject<AutoSendStatus | null>(null);
  readonly consentRequest$: Observable<AutoSendStatus | null> =
    this.consentRequestSubject.asObservable();

  readonly consentChanges$: Observable<ConsentState> = this.consent$.asObservable();
  readonly statusChanges$: Observable<AutoSendStatus> = this.status$.asObservable();
  /** What LogReporter subscribes to: `allowed` is true iff the user has
   *  granted consent. Server-side targeting does NOT gate uploads. */
  readonly autoSendAllowed$: Observable<{ allowed: boolean; status: AutoSendStatus }> =
    this.effective$.asObservable();

  private pollTimer: any = null;
  /**
   * Cached app version (e.g. "1.0.63") populated from
   * assets/settings/version.json on the first refreshStatus() and reused on
   * subsequent polls. Falls back to empty string if the JSON is unreachable.
   */
  private cachedAppVersion: string | undefined;
  /**
   * Monotonic counter bumped on every consent state mutation
   * (`grant` / `revoke` / `initOnBoot` policy invalidation). `refreshStatus`
   * captures it at entry and discards stale responses whose generation no
   * longer matches — prevents an in-flight HTTP call from resurrecting a
   * `consentRequest` after revoke.
   */
  private stateGeneration = 0;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
  ) {
    // Re-derive effective state whenever consent or status changes.
    this.consent$.subscribe(() => this.recomputeEffective());
    this.status$.subscribe(() => this.recomputeEffective());
  }

  /** Current snapshot (sync). */
  getConsent(): ConsentState { return this.consent$.value; }
  getStatus(): AutoSendStatus { return this.status$.value; }
  isAutoSendAllowed(): boolean { return this.effective$.value.allowed; }

  markFirstLaunchPromptSeen(): void {
    try {
      localStorage.setItem(DiagnosticsConsentService.FIRST_LAUNCH_PROMPT_KEY, "1");
    } catch (_) { }
  }

  /** User taps the toggle ON in Settings. */
  grant(): void {
    const state: ConsentState = {
      granted: true,
      version: DiagnosticsConsentService.CURRENT_POLICY_VERSION,
      grantedAt: Date.now(),
    };
    this.stateGeneration++;
    this.persistConsent(state);
    this.consent$.next(state);
    this.consentRequestSubject.next(null);
    // Once consented, auto-upload is unconditional → no need to poll the
    // targeting endpoint anymore. Stop polling to save battery + requests.
    this.stopPolling();
  }

  /** User taps OFF, OR policy version bumps (auto-invalidate). */
  revoke(): void {
    this.stateGeneration++;
    this.persistConsent({ granted: false, version: null, grantedAt: null });
    this.consent$.next({ granted: false, version: null, grantedAt: null });
    this.status$.next({ matched: false, active: false });
    this.consentRequestSubject.next(null);
    try { localStorage.removeItem(DiagnosticsConsentService.STATUS_CACHE_KEY); } catch (_) { }
    // The user explicitly said no — respect that. Suppress the consent-
    // request prompt for 30 days (they can still re-enable manually from
    // Settings any time). Without this, the next poll could immediately
    // re-prompt them, which is nagging.
    try {
      localStorage.setItem(
        DiagnosticsConsentService.DISMISS_KEY,
        String(Date.now() + 30 * 24 * 60 * 60 * 1000)
      );
    } catch (_) { }
    // Consent is now OFF again. Keep polling support targeting so a future
    // investigation can re-prompt after the dismissal window expires.
    this.startPolling();
  }

  /** Caller (AppComponent prompt) dismisses the in-app opt-in request. */
  dismissConsentRequest(): void {
    this.consentRequestSubject.next(null);
    // Remember dismissal for 24h so we don't nag the user multiple times.
    try {
      localStorage.setItem(
        DiagnosticsConsentService.DISMISS_KEY,
        String(Date.now() + 24 * 60 * 60 * 1000)
      );
    } catch (_) { }
  }

  /**
   * Called from AppComponent on boot.
   * - If consent is on an outdated policy version, auto-revoke.
   * - If consent is GRANTED: nothing to poll — auto-upload is unconditional.
   * - If consent is NOT granted: poll the targeting endpoint so we can show
   *   the in-app consent-request prompt when support targets this device.
   */
  initOnBoot(): void {
    const c = this.consent$.value;
    if (c.granted && c.version !== DiagnosticsConsentService.CURRENT_POLICY_VERSION) {
      this.revoke();
    }
    if (this.consent$.value.granted) {
      // Consented → auto-upload runs regardless of targeting. No polling.
      return;
    }
    this.maybeEmitFirstLaunchPrompt();
    if (!this.isAuthenticated()) {
      // Targeting endpoint requires a bearer token — skip until login.
      return;
    }
    // Not consented → show first-launch disclosure once, then keep checking
    // whether support is investigating this device's cohort.
    void this.refreshStatus();
    this.startPolling();
  }

  /**
   * Fetch the server's targeting verdict. ALWAYS runs (even when consent is
   * OFF) so we can surface an in-app prompt if a support investigation now
   * matches this device.
   *
   * Race safety: we capture a `generation` token at call entry; if `revoke()`
   * (or any state-mutating action) bumps the generation while the HTTP call
   * is in flight, we drop the response on the floor instead of overwriting
   * fresh state with stale data.
   */
  async refreshStatus(): Promise<AutoSendStatus> {
    if (!this.isAuthenticated()) {
      return this.status$.value;
    }
    const gen = this.stateGeneration;
    // Hydrate app version once (cheap static asset). Failure is silent —
    // the param just stays "" and server-side device_model/platform/user
    // matching still works.
    if (this.cachedAppVersion === undefined) {
      try {
        const v: any = await firstValueFrom(
          this.http.get("assets/settings/version.json")
        );
        if (v?.version) {
          this.cachedAppVersion = String(v.version).slice(0, 32);
        } else {
          this.cachedAppVersion = "";
        }
      } catch (_) {
        this.cachedAppVersion = "";
      }
    }
    try {
      const status = await firstValueFrom(this.fetchStatus());
      // Generation changed → state was mutated under us (revoke / grant / etc.).
      // Discard this response to avoid resurrecting a stale active flag.
      if (gen !== this.stateGeneration) {
        return this.status$.value;
      }
      this.status$.next(status);
      this.persistStatusCache(status);

      // Re-check consent at emit time (not at call entry) so a revoke that
      // landed during the in-flight HTTP call is honored.
      if (status.matched && !this.consent$.value.granted) {
        const dismissUntil = this.loadDismissUntil();
        if (Date.now() >= dismissUntil) {
          this.consentRequestSubject.next({
            ...status,
            promptContext: "support_target",
          });
        }
      } else {
        this.consentRequestSubject.next(null);
      }

      return status;
    } catch (_) {
      // Network errors are silent — keep cached state. LogReporter handles
      // HTTP errors gracefully if auto-send fires meanwhile.
      return this.status$.value;
    }
  }

  private fetchStatus(): Observable<AutoSendStatus> {
    const params: Record<string, string> = {
      app_version: this.cachedAppVersion ?? "",
      platform: this.detectPlatform(),
      device_model: this.detectDeviceModel(),
      // Server uses this to mark active=true ONLY when local consent is on.
      // The server cannot override; it can only request consent.
      has_consent: this.consent$.value.granted ? "1" : "0",
    };
    const qs = Object.entries(params)
      .filter(([, v]) => v)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
    const url = `${environment.authUrl}/frontoffice/diagnostics/auto-send-status${qs ? "?" + qs : ""}`;
    // Backend response uses snake_case `expires_at`; we normalize to camelCase
    // `expiresAt` for the rest of the mobile code. Both shapes accepted for
    // forward-compatibility if backend ever serializes camelCase.
    return this.http.get<any>(url).pipe(
      map((r) => ({
        matched: !!r?.matched,
        active: !!r?.active,
        expiresAt: r?.expires_at ?? r?.expiresAt,
        reason: r?.reason,
      } as AutoSendStatus)),
      catchError(() => of({ matched: false, active: false } as AutoSendStatus))
    );
  }

  private loadDismissUntil(): number {
    try {
      const raw = localStorage.getItem(DiagnosticsConsentService.DISMISS_KEY);
      return raw ? parseInt(raw, 10) || 0 : 0;
    } catch (_) {
      return 0;
    }
  }

  private maybeEmitFirstLaunchPrompt(): void {
    if (this.consent$.value.granted) {
      return;
    }
    if (Date.now() < this.loadDismissUntil()) {
      return;
    }
    try {
      if (localStorage.getItem(DiagnosticsConsentService.FIRST_LAUNCH_PROMPT_KEY)) {
        return;
      }
    } catch (_) {
      // If storage is unavailable, still show the disclosure once for this boot.
    }
    this.consentRequestSubject.next({
      matched: true,
      active: false,
      promptContext: "first_launch",
    });
  }

  private startPolling(): void {
    this.stopPolling();
    if (!this.isAuthenticated()) {
      return;
    }
    this.pollTimer = setInterval(
      () => void this.refreshStatus(),
      DiagnosticsConsentService.POLL_INTERVAL_MS
    );
  }

  private isAuthenticated(): boolean {
    return this.localStorageService.isAuth();
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private recomputeEffective(): void {
    // Auto-upload depends on USER CONSENT ALONE. Server-side targeting
    // (`status`) no longer gates the upload — it only drives the in-app
    // consent-request prompt for users who haven't opted in yet.
    const granted = this.consent$.value.granted;
    const status = this.status$.value;
    this.effective$.next({ allowed: granted, status });
  }

  private loadConsent(): ConsentState {
    try {
      const raw = localStorage.getItem(DiagnosticsConsentService.STORAGE_KEY);
      if (!raw) return { granted: false, version: null, grantedAt: null };
      const parsed = JSON.parse(raw);
      return {
        granted: !!parsed?.granted,
        version: typeof parsed?.version === "string" ? parsed.version : null,
        grantedAt: typeof parsed?.grantedAt === "number" ? parsed.grantedAt : null,
      };
    } catch (_) {
      return { granted: false, version: null, grantedAt: null };
    }
  }

  private persistConsent(state: ConsentState): void {
    try {
      localStorage.setItem(DiagnosticsConsentService.STORAGE_KEY, JSON.stringify(state));
    } catch (_) { }
  }

  private loadStatusCache(): AutoSendStatus | null {
    try {
      const raw = localStorage.getItem(DiagnosticsConsentService.STATUS_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Defensive: any `active:true` without a known expiry is treated as
      // stale (we don't want to display a banner forever if the server
      // never responds again).
      if (parsed?.active && !parsed?.expiresAt) {
        return null;
      }
      if (parsed?.expiresAt && Date.parse(parsed.expiresAt) < Date.now()) {
        return null;
      }
      return parsed;
    } catch (_) {
      return null;
    }
  }

  private persistStatusCache(status: AutoSendStatus): void {
    try {
      localStorage.setItem(
        DiagnosticsConsentService.STATUS_CACHE_KEY,
        JSON.stringify(status)
      );
    } catch (_) { }
  }

  private detectPlatform(): string {
    if (typeof navigator === "undefined") return "web";
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/i.test(ua)) return "ios";
    if (/Android/i.test(ua)) return "android";
    return "web";
  }

  /**
   * Best-effort device model extraction from the User-Agent. Android Chrome
   * embeds the model (e.g. "Build/RP1A...; Redmi 13C ..."); iOS WKWebView
   * doesn't expose the exact model (only "iPhone" / "iPad"). For exact iOS
   * model granularity, install @capacitor/device and call Device.getInfo().
   */
  private detectDeviceModel(): string {
    if (typeof navigator === "undefined") return "";
    const ua = navigator.userAgent || "";
    // Android pattern: "(Linux; Android 13; Redmi 13C Build/..."
    const m = ua.match(/Android[^;]*;\s*([^;)]+?)(?:\s+Build|;|\))/i);
    if (m && m[1]) {
      return m[1].trim().slice(0, 64);
    }
    if (/iPhone/i.test(ua)) return "iPhone";
    if (/iPad/i.test(ua)) return "iPad";
    if (/iPod/i.test(ua)) return "iPod";
    return "";
  }
}
